<template lang="pug">
div(ref="chatContainer")
  template(v-for="(chat, index) in messageList")
    ChatSeparator(
      v-if="!isSessionEnded && newMessageId === chat.id"
      :isRead="false"
      :isFixed="!isNewMessageVisible"
      @onReadNewMessage="readNewMessage"
    )
    ChatSeparator(v-if="shouldShowDatestamp(chat.timestamp)" :timestamp="chat.timestamp")
    BubbleChat(
      v-if="shouldShowChatBubble({ isChatbotCommand: chat.isChatbotCommand, isChatbotOptions: !!chat.options.length })"
      ref="bubbleChat"
      :isMe="chat.nick === 'visitor'"
      :textChat="chat.msg"
      :showMeta="true"
      :timestamp="chat.timestamp"
      :status="chat.status"
      :imgUrl="chat.imgUrl"
      :imgName="chat.imgName"
      :imgSize="chat.imgSize"
      :nick="chat.nick"
      :type="chat.type"
      :shouldShowTimestamp="shouldShowTimestamp({ currentChat: chat, nextChat: getNextChat(index) })"
      :shouldShowAvatar="shouldShowAvatar({ currentChat: chat, nextChat: getNextChat(index) })"
      :chatIndex="index"
      :messageLength="messageList.length"
      @resend="resendChat"
      @media-thumbnail-click="onMediaThumbnailClick"
    )
  ChatSeparator(v-if="isSessionEnded" :text="`${getCSAgentName} keluar dari percakapan`")
  ChatRate(v-if="isFeedbackRequested" @ratingReceived="rateChatExperience")
  MediaPreview(v-show="mediaPreview.active" :url="mediaPreview.url" :mediaType="mediaPreview.mediaType" @close="toggleMediaPreview(false)")
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { humanizeTimestamp } from '~/utils/date-helper';
import { chatFunctions } from '~/utils/chat-functions';
import { debounce } from '~/utils/timer-util';
import scrollMixin from '~/mixins/scroll';
import chatbotMixin from '~/mixins/chatbot';
import chatMixin from '~/mixins/chat';

import BubbleChat from '~/components/chat/Bubble';
import ChatRate from '~/components/chat/Rate';
import MediaPreview from '~/components/chat/MediaPreview';
import ChatSeparator from '~/components/ChatSeparator';

import messageListEventBus from './messageListEventBus';

const FOOTER_HEIGHT = 60;
const CHAT_LAST_READS_KEY = 'chat-last-reads';
const NEW_MESSAGE_DETECTION_HEIGHT = 100;

export default {
  name: 'MessageList',
  components: {
    BubbleChat,
    ChatRate,
    MediaPreview,
    ChatSeparator,
  },
  mixins: [scrollMixin, chatbotMixin, chatMixin],
  data() {
    return {
      visitorName: '',
      timestampList: {
        visitor: {},
        agent: {},
      },
      datestampList: {},
      textFeedback: '',
      lastReadOffset: 0,
      newMessageId: 0,
      newMessageOffset: 0,
      isNewMessageVisible: false,

      // For media preview overlay
      mediaPreview: {
        active: false,
        url: '',
        mediaType: '',
      },
    };
  },
  computed: {
    ...mapGetters({
      isOnline: 'zopim/app/isOnline',
      chatSession: 'zopim/app/chatSession',
      messageList: 'zopim/message/getList',
      isFeedbackRequested: 'zopim/message/isFeedbackRequested',
      isFeedbackGiven: 'zopim/message/isFeedbackGiven',
      isFeedbackTextGiven: 'zopim/message/isFeedbackTextGiven',
      isFeedbackSkipped: 'zopim/message/isFeedbackSkipped',
      lastMessage: 'zopim/message/getLastMessage',
      isUserLoggedIn: 'auth/isUserLoggedIn',
      getCSAgentNick: 'zopim/agent/getCSAgentNick',
      getCSAgentName: 'zopim/agent/getCSAgentName',
      user: 'user/getMe',
      changePhoneDataValid: 'change-phone-number/isDataValid',
      isInLiveChatSession: 'zopim/app/isInLiveChatSession',
      isSessionEnded: 'zopim/app/isSessionEnded',
      isWaitingAgent: 'zopim/app/isWaitingAgent',
      isAgentAcceptTicket: 'zopim/app/isAgentAcceptTicket'
    }),

    /**
     * @returns {number}
     */
    messageListLength() {
      return this.messageList.length;
    },

    /**
     * @returns {boolean}
     */
    isFromChangePhoneNumber() {
      return this.$route.query.changePhoneNumber === 'true';
    },
  },
  watch: {
    messageList() {
      if (this.messageList.length <= 2) {
        this.clearLastReadOffset();
      }
      this.updateTimestampList();
      this.updateDatestampList();
      this.getLastReadOffset();
      this.getNewMessageOffset();
    },

    lastReadOffset() {
      this.saveLastReadOffset();
    },

    isOnline(newVal) {
      if (newVal) {
        const chatLog = this.$zopim?.getChatLog();
        if (chatLog?.length > 0) {
          chatLog.forEach(chat => {
            if (!chatFunctions[chat.type]) return;
            this[chatFunctions[chat.type]](chat);
          });
        }
      }
    },
  },
  mounted() {
    window.addEventListener('scroll', this.getLastReadOffset.bind(this), { passive: true });
    this.getLastReadOffset();
    messageListEventBus.$on('listenChat', detail => {
      if (!chatFunctions[detail.type]) return;
      this[chatFunctions[detail.type]](detail);
    });
  },
  destroyed() {
    window.removeEventListener('scroll', this.getLastReadOffset.bind(this), { passive: true });
    messageListEventBus.$off('listenChat');
  },
  methods: {
    ...mapActions({
      sendMessage: 'zopim/message/sendMessage',
      retrieveUser: 'user/getMe',
      setFeedbackRequestState: 'zopim/message/setFeedbackRequestState',
      setFeedbackGivenState: 'zopim/message/setFeedbackGivenState',
      setFeedbackTextGiven: 'zopim/message/setFeedbackTextGiven',
      setAgentNick: 'zopim/agent/setAgentNick',
      setAgentName: 'zopim/agent/setAgentName',
      setFeedbackTextRequested: 'zopim/message/setFeedbackTextRequested',
      setWaitingAgentStatus: 'zopim/app/setWaitingAgentStatus',
      setQueuePosition: 'zopim/app/setQueuePosition',
      endSession: 'zopim/app/endSession',
    }),
    getLastReadOffset() {
      if (!this.lastReadOffset) {
        this.lastReadOffset = JSON.parse(window.localStorage.getItem(CHAT_LAST_READS_KEY))?.livechat ?? 0;
      }
      const currentViewport = window.scrollY + document.body.offsetHeight - FOOTER_HEIGHT;
      if (currentViewport > this.lastReadOffset) this.lastReadOffset = currentViewport;
      this.isNewMessageVisible = currentViewport > this.newMessageOffset;
    },
    async getNewMessageOffset() {
      await this.$nextTick();
      this.messageList.forEach(chat => {
        const element = this.$refs.bubbleChat?.[chat.id - 1]?.$el;
        const chatOffset = element?.offsetTop;

        if (
          this.lastReadOffset - NEW_MESSAGE_DETECTION_HEIGHT < chatOffset &&
          chatOffset < this.lastReadOffset + NEW_MESSAGE_DETECTION_HEIGHT
        ) {
          if (!this.newMessageOffset) {
            this.newMessageId = chat.id;
            this.newMessageOffset = chatOffset;
          }
        }

        setTimeout(() => {
          if (this.isInLiveChatSession && element && element.scrollIntoView) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        });
      });

      this.getLastReadOffset();
    },
    readNewMessage() {
      this.scrollTo(this.newMessageOffset - FOOTER_HEIGHT);
    },
    clearLastReadOffset() {
      this.lastReadOffset = 0;
      this.executeSaveLastReadOffset();
    },
    // eslint-disable-next-line @typescript-eslint/space-before-function-paren
    saveLastReadOffset: debounce(function() {
      this.executeSaveLastReadOffset();
    }, 500),
    executeSaveLastReadOffset() {
      let chatLastReads = JSON.parse(window.localStorage.getItem(CHAT_LAST_READS_KEY));
      chatLastReads = {
        ...chatLastReads,
        livechat: this.lastReadOffset,
      };
      window.localStorage.setItem(CHAT_LAST_READS_KEY, JSON.stringify(chatLastReads));
    },
    resendChat(_data) {
      // TODO: this hasn't implemented
      // console.table(data);
      // this.sendMessage({
      //   msg: data.textChat,
      //   imgUrl: data.imgUrl,
      //   timestamp: data.timestamp,
      //   nick: data.nick,
      // });
    },
    rateChatExperience(rating) {
      this.setFeedbackRequestState(false);
      this.setFeedbackGivenState(true);
      this.$zopim.sendChatRating(rating);
    },
    isVisitor(nick) {
      return nick === 'visitor';
    },
    timeString(timestamp) {
      return humanizeTimestamp(timestamp, '%hh%:%mi%');
    },
    updateTimestampList() {
      this.messageList.forEach(e => {
        const now = this.timeString(e.timestamp);
        const user = this.isVisitor(e.nick) ? 'visitor' : 'agent';
        this.timestampList[user][now] = e.timestamp;
      });
    },
    updateDatestampList() {
      this.messageList.forEach(e => {
        const dateNow = humanizeTimestamp(e.timestamp, '%day%, %dd% %month%');
        if (!this.datestampList[dateNow]) this.datestampList[dateNow] = e.timestamp;
      });
    },

    /**
     * @returns {object}
     */
    getNextChat(currentIndex) {
      return this.messageList[currentIndex + 1];
    },

    /**
     * @param {boolean} param.isChatbotCommand
     * @param {boolean} param.isChatbotOptions
     * @returns {boolean}
     */
    shouldShowChatBubble({ isChatbotCommand, isChatbotOptions }) {
      return !isChatbotCommand && !isChatbotOptions;
    },

    /**
     * @param {object} param.currentChat
     * @param {object} param.nextChat
     * @returns {boolean}
     */
    shouldShowTimestamp({ currentChat, nextChat }) {
      const isMe = currentChat.nick === 'visitor';
      const convertedTime = this.timeString(currentChat.timestamp);
      const isNextChatCommandOrOptions = !!(nextChat?.isChatbotCommand || nextChat?.options.length);

      const user = isMe ? 'visitor' : 'agent';
      return this.timestampList[user][convertedTime] === currentChat.timestamp || isNextChatCommandOrOptions;
    },

    shouldShowDatestamp(timestamp) {
      const dateNow = humanizeTimestamp(timestamp, '%day%, %dd% %month%');
      return this.datestampList[dateNow] === timestamp;
    },

    /**
     * Decides to show/hide avatars
     * @param {object} param.currentChat
     * @param {object} param.nextChat
     * @returns {boolean}
     */
    shouldShowAvatar({ currentChat, nextChat }) {
      const nextChatHasDifferentNick = currentChat.nick !== nextChat?.nick;
      const isCurrentChatCommand = currentChat.isChatbotCommand;
      const isNextChatCommand = !!nextChat?.isChatbotCommand;
      const isNextChatOptions = !!nextChat?.options?.length;
      const isLastChat = !nextChat;

      let result = false;
      if (nextChatHasDifferentNick) {
        result = !isCurrentChatCommand || isLastChat;
        return result;
      }

      result = isNextChatCommand || isNextChatOptions || isLastChat;
      return result;
    },

    /**
     * Handles event @thumbnail-click emitted by Bubble.vue
     * Will populate url and mediaType for mediaPreview
     * @param {object} - Value emitted from the Bubble.vue
     * @param {string} param.url
     * @param {string} param.mediaType
     */
    onMediaThumbnailClick({ url = '', mediaType = '' }) {
      this.mediaPreview = {
        ...this.mediaPreview,
        active: true,
        url,
        mediaType,
      };
    },

    /**
     * Show/hide MediaPreview
     * @param {boolean} active
     */
    toggleMediaPreview(active = true) {
      this.mediaPreview = {
        ...this.mediaPreview,
        active,
        url: '',
        mediaType: '',
      };
    },

    typing(detail) {
      const isCSTyping = detail.typing;
      messageListEventBus.$emit('agentTyping', isCSTyping);
    },
    getGreeting() {
      const currentHour = new Date().getHours();
      if (currentHour < 6) return 'malam';
      if (currentHour < 10) return 'pagi';
      if (currentHour < 14) return 'siang';
      if (currentHour < 18) return 'sore';
      return 'malam';
    },
    memberJoin(detail) {
      if (this.isVisitor(detail.nick)) {
        this.visitorName = (this.user && this.user.name) || detail.display_name;
        if (this.messageList.length <= 1) {
          this.sendMessage({
            ...detail,
            msg: `Selamat ${this.getGreeting()}, ${this.visitorName}. BukaBantuan siap membantumu 😊`,
            nick: 'greeting',
          });
        }
      } else if (this.getCSAgentNick === '' || this.getCSAgentName === '') {
        this.setAgentName(detail.display_name);
        this.setAgentNick(detail.nick);
      }
    },
    memberLeave(detail) {
      if (this.isVisitor(detail.nick)) return;
      this.endSession();
      this.scrollTo('bottom');
    },
    chatArrives(detail) {
      // process detail to do chatbot things if necessary, then send message
      this.processDetailToChatbotLogic(detail);
      this.sendMessage(detail);
    },
    chatRating(detail) {
      let ratingMessage = '';
      let csReply = '';
      if (detail.new_rating === 'good') {
        ratingMessage = 'Saya dapat pelayanan yang baik. Terima kasih 👍';
        csReply = 'Wah, terima kasih! Boleh ceritakan lebih lanjut bagaimana pelayanan kami membantu masalah kamu?';
      } else {
        ratingMessage = 'Saya kecewa dengan pelayanan CS tadi 🙁';
        csReply = 'Mohon maaf atas kekurangan kami. Boleh tahu, apa yang bisa ditingkatkan dari pelayanan kami?';
      }
      this.sendMessage({
        msg: ratingMessage,
        ...detail,
      });

      if (!this.isSessionEnded) {
        const csDetail = {
          display_name: this.getCSAgentName,
          msg: csReply,
          nick: this.getCSAgentNick,
          options: [],
          timestamp: Date.now(),
          type: 'chat.msg',
        };

        this.sendMessage({
          msg: csReply,
          ...csDetail,
        });

        csDetail.msg =
          'Silakan sampaikan pesan kamu dalam sekali ketik. Bila kamu mengirim pesan lebih dari sekali, pesan yang kedua dan seterusnya tidak akan tercatat.';
        csDetail.timestamp = Date.now();

        this.sendMessage({
          msg: csReply,
          ...csDetail,
        });
      }

      this.setFeedbackRequestState(false);
      this.setFeedbackGivenState(true);
      this.setFeedbackTextRequested(true);
      this.scrollTo('bottom');
    },
    chatComment(_detail) {
      const csReply =
        'Terima kasih atas masukannya! Pendapatmu akan sangat berguna untuk meningkatkan pelayanan CS kami ke depannya 🤝';

      const csDetail = {
        display_name: this.getCSAgentName,
        msg: csReply,
        nick: this.getCSAgentNick,
        options: [],
        timestamp: Date.now(),
        type: 'chat.msg',
      };

      this.sendMessage({
        msg: csReply,
        ...csDetail,
      });
      this.setFeedbackTextGiven(true);
    },
    requestRating(detail) {
      if (!this.isVisitor(detail.nick) && !this.isFeedbackGiven && !this.isFeedbackTextGiven) {
        this.setFeedbackRequestState(true);
        this.scrollTo('bottom');
      } else {
        this.setFeedbackRequestState(false);
      }
    },
    fileAttachmentArrived(detail) {
      this.sendMessage({
        imgUrl: detail.attachment.url,
        imgName: detail.attachment.name,
        imgSize: detail.attachment.size,
        ...detail,
      });
    },
    revealQueuePos(detail) {
      if (this.chatSession !== 'agent') return;
      this.setQueuePosition(detail.queue_position);

      if (detail.queue_position === 0 && this.isWaitingAgent) {
        this.setWaitingAgentStatus('found-agent');
        setTimeout(() => {
          this.setWaitingAgentStatus('taken');
        }, 1000);
      }
    },
  },
};
</script>
